'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// ─── useInView ────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);

  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView] as const;
}

const MOTOR_BRANDS = [
  {
    flag: '🇮🇳',
    label: 'Indian Made',
    tagline: 'Best value',
    color: '#E07B39',
    bg: 'rgba(224,123,57,0.08)',
  },
  {
    flag: '🇨🇳',
    label: 'Dooya',
    tagline: 'Smart & silent',
    color: '#2A6EBB',
    bg: 'rgba(42,110,187,0.08)',
  },
  {
    flag: '🇫🇷',
    label: 'Somfy',
    tagline: 'Luxury grade',
    color: '#8B1A1A',
    bg: 'rgba(139,26,26,0.08)',
  },
];

 

// Swap for your own hosted video for production
const VIDEO_SRC = 'https://res.cloudinary.com/dsizaxsjm/video/upload/v1779014930/motorized_r94lde.mp4';
// Fallback poster image (Cloudinary from the PF website)
const FALLBACK_POSTER = 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773518351/home_hero_xkp8dy.jpg';

export default function MotorizedBlindsHomeSection() {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [sectionRef, inView] = useInView(0.1);

  useEffect(() => {
    if (inView && videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;
      videoRef.current.play().catch(() => {/* autoplay blocked */});
    }
  }, [inView]);

  return (
    <section data-component-id="src/app/homepage/components/MotorizedSection.tsx:67:4" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="67" data-component-end-line="352" data-component-file="MotorizedSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22bg-primary%22%7D"
      ref={sectionRef} 
      className="bg-primary"
      style={{
        position: 'relative',
        overflow: 'hidden',
        
        minHeight: 620, 
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* ── Video Background ─────────────────────────────────────────── */}
        <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:80:8" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="80" data-component-end-line="108" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" 
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}>
        <video data-component-id="src/app/homepage/components/MotorizedSection.tsx:87:8" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="87" data-component-end-line="107" data-component-file="MotorizedSection.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22src%22%3A%22%5Bvar%3AVIDEO_SRC%5D%22%7D"
          ref={videoRef}
          src={VIDEO_SRC}
          poster={FALLBACK_POSTER}
          autoPlay
          preload="auto"
          muted
          loop
          playsInline
          onCanPlay={() => {
            if (videoRef.current) {
              videoRef.current.play().catch(() => {/* autoplay blocked */});
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.35,
          }}
        />
      </div>

      {/* ── Content ──────────────────────────────────────────────────── */}
      <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:111:6" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="111" data-component-end-line="297" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
        position: 'relative',
        zIndex: 10,
        maxWidth: 1200,
        margin: '0 auto',
        padding: '80px 2rem',
        width: '100%',
      }}>
        <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:119:8" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="119" data-component-end-line="296" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{ maxWidth: 640 }}>

          {/* Label pill */}
          <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:122:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="122" data-component-end-line="143" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255,216,100,0.12)',
            border: '1px solid rgba(255,216,100,0.25)',
            borderRadius: 100,
            padding: '5px 14px',
            marginBottom: 28,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}>
            <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:135:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="135" data-component-end-line="139" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#FFD864',
              animation: 'motorPulse 2s ease-in-out infinite',
            }} />
            <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:140:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="140" data-component-end-line="142" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Best%20Seller%22%7D" style={{ color: '#FFD864', fontSize: 11, letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase' }}>
              Best Seller
            </span>
          </div>

          {/* Heading */}
          <h2 data-component-id="src/app/homepage/components/MotorizedSection.tsx:146:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="146" data-component-end-line="165" data-component-file="MotorizedSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Motorized%20Blinds%20%E2%80%94%22%7D" style={{
            fontSize: 'clamp(2rem, 5vw, 3.4rem)',
            fontWeight: 800,
            color: '#FFFFFF',
            lineHeight: 1.1,
            margin: '0 0 20px',
            fontFamily: "'Playfair Display', Georgia, serif",
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}>
            Motorized Blinds —<br data-component-id="src/app/homepage/components/MotorizedSection.tsx:157:30" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="157" data-component-end-line="157" data-component-file="MotorizedSection.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
            <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:158:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="158" data-component-end-line="164" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Control%20Light%20Effortlessly%22%7D" style={{
              background: 'linear-gradient(90deg, #FFD864, #FF9F43)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Control Light Effortlessly
            </span>
          </h2>

          {/* Description */}
          <p data-component-id="src/app/homepage/components/MotorizedSection.tsx:168:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="168" data-component-end-line="180" data-component-file="MotorizedSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Open%20or%20close%20your%20blinds%20with%20a%20tap%2C%20a%20voice%20command%2C%20or%20a%20schedule.%5Cn%20%20%20%20%20%20%20%20%20%20%20%20Choose%20from%20three%20tiers%20%E2%80%94%20locally%20made%2C%20Dooya%20smart%20motors%2C%20or%20Somfy%20luxury%20grade.%5Cn%20%20%20%20%20%20%20%20%20%20%20%20Our%20most%20popular%20upgrade%20for%20homes%20and%20offices.%22%7D" style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '1.05rem',
            lineHeight: 1.7,
            margin: '0 0 40px',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}>
            Open or close your blinds with a tap, a voice command, or a schedule.
            Choose from three tiers — locally made, Dooya smart motors, or Somfy luxury grade.
            Our most popular upgrade for homes and offices.
          </p>

          {/* Brand pills */}
          <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:183:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="183" data-component-end-line="217" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 40,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s',
          }}>
            {MOTOR_BRANDS.map((b) => (
              <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:193:14" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="193" data-component-end-line="215" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={b.label} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: b.bg,
                border: `1px solid ${b.color}33`,
                borderRadius: 100,
                padding: '7px 14px',
              }}>
                <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:202:16" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="202" data-component-end-line="202" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" style={{ fontSize: '1rem' }}>{b.flag}</span>
                <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:203:16" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="203" data-component-end-line="203" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600 }}>{b.label}</span>
                <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:204:16" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="204" data-component-end-line="214" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" style={{
                  background: b.color,
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 100,
                  letterSpacing: 0.5,
                }}>
                  {b.tagline}
                </span>
              </div>
            ))}
          </div>

          {/* Feature chips */}
          <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:220:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="220" data-component-end-line="241" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 48,
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.8s ease 0.4s',
          }}>
            {['📱 App Control', '🗣️ Voice Control', '🕹️ Remote', '⏰ Scheduling', '🏠 Smart Home'].map((chip) => (
              <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:229:14" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="229" data-component-end-line="239" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" key={chip} style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12,
                fontWeight: 500,
                padding: '5px 12px',
                borderRadius: 6,
              }}>
                {chip}
              </span>
            ))}
          </div>

          {/* CTA Buttons */}
          <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:244:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="244" data-component-end-line="295" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
            display: 'flex',
            gap: 14,
            flexWrap: 'wrap',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
          }}>
            <Link data-component-id="src/app/homepage/components/MotorizedSection.tsx:252:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="252" data-component-end-line="273" data-component-file="MotorizedSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fmotorized-blinds%22%2C%22textContent%22%3A%22Explore%20Motorized%20Blinds%22%7D"
              href="/motorized-blinds"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'linear-gradient(135deg, #FFD864, #FF9F43)',
                color: '#1A1A1A',
                fontWeight: 700,
                fontSize: 15,
                padding: '14px 28px',
                borderRadius: 8,
                textDecoration: 'none',
                letterSpacing: 0.3,
                boxShadow: '0 4px 24px rgba(255,159,67,0.35)',
              }}
            >
              Explore Motorized Blinds
              <svg data-component-id="src/app/homepage/components/MotorizedSection.tsx:270:14" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="270" data-component-end-line="272" data-component-file="MotorizedSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path data-component-id="src/app/homepage/components/MotorizedSection.tsx:271:16" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="271" data-component-end-line="271" data-component-file="MotorizedSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>

            <a data-component-id="src/app/homepage/components/MotorizedSection.tsx:275:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="275" data-component-end-line="294" data-component-file="MotorizedSection.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Fwa.me%2F%2B919743104463%3Ftext%3DHi!%20I'm%20interested%20in%20motorized%20blinds.%22%2C%22textContent%22%3A%22%F0%9F%92%AC%20Get%20Quote%20on%20WhatsApp%22%7D"
              href="https://wa.me/+919743104463?text=Hi! I'm interested in motorized blinds."
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: 15,
                padding: '14px 28px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              💬 Get Quote on WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* ── Right side floating cards (desktop) ─────────────────────── */}
      <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:300:6" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="300" data-component-end-line="335" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{
        position: 'absolute',
        right: '5%',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        zIndex: 10,
        opacity: inView ? 1 : 0,
        transition: 'opacity 1s ease 0.6s',
      }}>
        {[
          { emoji: '🤫', val: '32 dB', label: 'Whisper Silent' },
          { emoji: '⚡', val: '5 Yr', label: 'Warranty' },
          { emoji: '🏠', val: '500+', label: 'Installs Done' },
        ].map((card, i) => (
          <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:317:10" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="317" data-component-end-line="333" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={card.label} style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 14,
            padding: '18px 22px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            animation: inView ? `motorFloat ${2.5 + i * 0.3}s ease-in-out ${i * 0.4}s infinite` : 'none',
          }}>
            <span data-component-id="src/app/homepage/components/MotorizedSection.tsx:328:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="328" data-component-end-line="328" data-component-file="MotorizedSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" style={{ fontSize: '1.5rem' }}>{card.emoji}</span>
            <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:329:12" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="329" data-component-end-line="332" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:330:14" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="330" data-component-end-line="330" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{ color: '#FFD864', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1 }}>{card.val}</div>
              <div data-component-id="src/app/homepage/components/MotorizedSection.tsx:331:14" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="331" data-component-end-line="331" data-component-file="MotorizedSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <style data-component-id="src/app/homepage/components/MotorizedSection.tsx:337:6" data-component-path="src/app/homepage/components/MotorizedSection.tsx" data-component-line="337" data-component-end-line="351" data-component-file="MotorizedSection.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D">{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@800&display=swap');
        @keyframes motorPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
        @keyframes motorFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        /* Hide floating cards on small screens */
        @media (max-width: 900px) {
          .motor-float-cards { display: none !important; }
        }
      `}</style>
    </section>
  );
}
